<template>
  <b-container fluid>
    <b-row>
      <b-col class="p-4">
        <h2 class="text-left">Courses
          <span class="float-right">
            <b-button :to="{ name: 'Import' }" variant="link" style="display: inline;">Import</b-button>
            <course-form :text="'Create'" :course="newCourse" v-on:ok="create" style="display: inline;"></course-form>
          </span>
        </h2>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="p-4">
        <b-input-group>
          <b-form-input
            v-model="searchText"
            placeholder="Search"
            type="text"
            debounce="500">
          </b-form-input>
          <b-input-group-append>
            <b-button v-on:click="searchText = ''"><b-icon-x></b-icon-x></b-button>
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>
     <b-row>
       <b-col class="text-right">
        <b-pagination v-model="pagination.page" :total-rows="pagination.total" :per-page="pagination.pageSize" align="right" v-if="!loading" @change="filterData"></b-pagination>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="p-4">
        <b-table small :items="courses" :fields="fields" :busy="loading" class="text-left">
          <template v-slot:table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong>Loading...</strong>
            </div>
          </template>

          <template v-slot:head(title)="data">
            <b-icon id="filter-title" :icon="titleFilter.text === '' ? 'funnel' : 'funnel-fill'"></b-icon>
            {{data.label}}
            <b-icon @click="sort(titleFilter)" :icon="titleFilter.sortOrder === 1 ? 'arrow-up' : titleFilter.sortOrder === 2 ? 'arrow-down': 'arrow-down-up'"></b-icon>
            <b-popover target="filter-title" triggers="click" placement="bottom">
              <template v-slot:title>Filter by Title</template>
              <b-form-group label="Title">
                <b-input type="text" v-model="titleFilter.text"></b-input>
              </b-form-group>
              <b-form-group>
                <b-form-select v-model="titleFilter.queryOperator">
                  <b-form-select-option :value="0"></b-form-select-option>
                  <b-form-select-option :value="1">Equals</b-form-select-option>
                  <b-form-select-option :value="2">Contains</b-form-select-option>
                  <b-form-select-option :value="3">Starts With</b-form-select-option>
                  <b-form-select-option :value="4">Ends With</b-form-select-option>
                </b-form-select>
              </b-form-group>
              <b-button-group>
                <b-button @click="clearFilter('titleFilter', { sortOrder: 0, queryOperator: 0, text: '' })">Clear</b-button>
                <b-button @click="filterData(1)">Filter</b-button>
                <b-button @click="$root.$emit('bv::hide::popover')">Cancel</b-button>
              </b-button-group>
            </b-popover>
          </template>

          <template v-slot:head(startDateTime)="data">
            <b-icon id="filter-start-date" :icon="startDateFilter.beginDate === null ? 'funnel' : 'funnel-fill'"></b-icon>
            {{data.label}}
            <b-icon @click="sort(startDateFilter)" :icon="startDateFilter.sortOrder === 1 ? 'arrow-up' : startDateFilter.sortOrder === 2 ? 'arrow-down': 'arrow-down-up'"></b-icon>
            <b-popover target="filter-start-date" triggers="click" placement="bottom">
              <template v-slot:title>Filter by Start Date</template>
              <b-form-group>
                <b-form-select v-model="startDateFilter.queryOperator">
                  <b-form-select-option :value="0"></b-form-select-option>
                  <b-form-select-option :value="1">Equal to selected date</b-form-select-option>
                  <b-form-select-option :value="2">After selected date</b-form-select-option>
                  <b-form-select-option :value="3">Before selected date</b-form-select-option>
                  <b-form-select-option :value="4">Between selected dates</b-form-select-option>
                </b-form-select>
              </b-form-group>
              <b-form-group v-if="startDateFilter.queryOperator != 4" label="Start Date">
                <b-form-datepicker v-model="startDateFilter.beginDate"></b-form-datepicker>
              </b-form-group>
              <b-form-group v-if="startDateFilter.queryOperator == 4" label="Begin Date">
                <b-form-datepicker v-model="startDateFilter.beginDate"></b-form-datepicker>
              </b-form-group>
              <b-form-group v-if="startDateFilter.queryOperator == 4" label="End Date">
                <b-form-datepicker v-model="startDateFilter.endDate"></b-form-datepicker>
              </b-form-group>
              <b-button-group>
                <b-button @click="clearFilter('startDateFilter', { sortOrder: 0, queryOperator: 0, beginDate: null, endDate: null })">Clear</b-button>
                <b-button @click="filterData(1)">Filter</b-button>
                <b-button @click="$root.$emit('bv::hide::popover')">Cancel</b-button>
              </b-button-group>
            </b-popover>
          </template>

          <template v-slot:head(endDateTime)="data">
            <b-icon id="filter-end-date" :icon="endDateFilter.beginDate === null ? 'funnel' : 'funnel-fill'"></b-icon>
            {{data.label}}
            <b-icon @click="sort(endDateFilter)" :icon="endDateFilter.sortOrder === 1 ? 'arrow-up' : endDateFilter.sortOrder === 2 ? 'arrow-down': 'arrow-down-up'"></b-icon>
            <b-popover target="filter-end-date" triggers="click" placement="bottom">
              <template v-slot:title>Filter by End Date</template>
              <b-form-group>
                <b-form-select v-model="endDateFilter.queryOperator">
                  <b-form-select-option :value="0"></b-form-select-option>
                  <b-form-select-option :value="1">Equal to selected date</b-form-select-option>
                  <b-form-select-option :value="2">After selected date</b-form-select-option>
                  <b-form-select-option :value="3">Before selected date</b-form-select-option>
                  <b-form-select-option :value="4">Between selected dates</b-form-select-option>
                </b-form-select>
              </b-form-group>
              <b-form-group v-if="endDateFilter.queryOperator != 4" label="End Date">
                <b-form-datepicker v-model="endDateFilter.beginDate"></b-form-datepicker>
              </b-form-group>
              <b-form-group v-if="endDateFilter.queryOperator == 4" label="Begin Date">
                <b-form-datepicker v-model="endDateFilter.beginDate"></b-form-datepicker>
              </b-form-group>
              <b-form-group v-if="endDateFilter.queryOperator == 4" label="End Date">
                <b-form-datepicker v-model="endDateFilter.endDate"></b-form-datepicker>
              </b-form-group>
              <b-button-group>
                <b-button @click="clearFilter('endDateFilter', { sortOrder: 0, queryOperator: 0, beginDate: null, endDate: null })">Clear</b-button>
                <b-button @click="filterData(1)">Filter</b-button>
                <b-button @click="$root.$emit('bv::hide::popover')">Cancel</b-button>
              </b-button-group>
            </b-popover>
          </template>

          <template v-slot:cell(title)="data">
            <b-button variant="link" :to="{ name: 'Course', params: { id: data.item.id }}" class="text-left">{{data.value}}</b-button>
          </template>

          <template v-slot:cell(trainingEventId)="data">
            {{data.value}}
          </template>

          <template v-slot:cell(meetingId)="data">
            <a :href="data.value" target="blank" class="btn btn-link">{{data.value}}</a>
          </template>

          <template v-slot:cell(startDateTime)="data">
            {{data.value | formatDate('MMM DD, YYYY')}}
          </template>

          <template v-slot:cell(endDateTime)="data">
            {{data.value | formatDate('MMM DD, YYYY')}}
          </template>

          <template v-slot:cell(actions)="data">
            <b-button-group>
              <course-form :text="'Edit'" :course="data.item" v-on:ok="save" style="display: inline;"></course-form>
              <course-form :text="'Copy'" :course="data.item" v-on:ok="copy" style="display: inline;"></course-form>
              <b-button @click="deleteCourse(data.item)" variant="link" style="display: inline;">Delete</b-button>
            </b-button-group>
          </template>
        </b-table>
      </b-col>
    </b-row>
     <b-row>
       <b-col class="text-right">
        <b-pagination v-model="pagination.page" :total-rows="pagination.total" :per-page="pagination.pageSize" align="right" v-if="!loading" @change="filterData"></b-pagination>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import Vue from 'vue'
import moment from 'moment'
import Api from '@/api/Api'
import CourseForm from './components/CourseForm'

Vue.filter('formatDate', function (value, format) {
  if (moment(value).isValid() && moment(value).year() > 1) {
    if (format) {
      return moment(value).format(format)
    } else {
      return moment(value).format('MMMM Do YYYY hh:mm a')
    }
  } else {
    return ''
  }
})

export default {
  name: 'CoursesPage',
  data () {
    return {
      fields: [ 'title', 'trainingEventId', { key: 'meetingId', label: 'Meeting Link' }, 'startDateTime', 'endDateTime', { key: 'actions', label: '' } ],
      courses: [],
      loading: true,
      searchText: '',
      newCourse: {
        id: 0,
        title: '',
        meetingId: '',
        trainingEventId: '',
        startDateTime: moment(`${moment().format('YYYY-MM-DD')} 8:00 AM`),
        endDateTime: moment(`${moment().format('YYYY-MM-DD')} 12:00 PM`).add(2, 'days'),
        showSurvey: true
      },
      pagination: {
        pageSize: 25,
        page: 1,
        total: 0
      },
      titleFilter: {
        sortOrder: 0,
        queryOperator: 0,
        text: ''
      },
      startDateFilter: {
        beginDate: null,
        endDate: null,
        queryOperator: 0,
        sortOrder: 2
      },
      endDateFilter: {
        beginDate: null,
        endDate: null,
        queryOperator: 0,
        sortOrder: 0
      }
    }
  },
  created () {
    this.filterData(1)
  },
  methods: {
    filterData (page) {
      this.$root.$emit('bv::hide::popover')
      var vm = this
      vm.loading = true
      vm.pagination.page = page
      Api().post('Course/Query', {
        page: page,
        pageSize: vm.pagination.pageSize,
        titleFilter: vm.titleFilter,
        startDateFilter: {
          ...vm.startDateFilter,
          beginDate: vm.startDateFilter.beginDate == null ? new Date() : vm.startDateFilter.beginDate,
          endDate: vm.startDateFilter.endDate == null ? new Date() : vm.startDateFilter.endDate
        },
        endDateFilter: {
          ...vm.endDateFilter,
          beginDate: vm.endDateFilter.beginDate == null ? new Date() : vm.endDateFilter.beginDate,
          endDate: vm.endDateFilter.endDate == null ? new Date() : vm.endDateFilter.endDate
        }
      })
        .then((r) => {
          vm.courses = r.data.items
          vm.pagination.total = r.data.total
          vm.pagination.pageSize = r.data.pageSize
        })
        .catch((e) => {
          if (e.response.status === 401) {
            this.$router.push({ name: 'Login' })
          } else {
            console.log(e)
          }
        })
        .finally(() => {
          vm.loading = false
        })
    },
    create (result) {
      var vm = this
      vm.loading = true
      Api().post('Course', result.course)
        .then((r) => {
          vm.courses.push(r.data)
        })
        .finally(() => {
          vm.loading = false
          vm.newCourse = {
            id: 0,
            title: '',
            meetingId: '',
            trainingEventId: '',
            startDateTime: moment(`${moment().format('YYYY-MM-DD')} 8:00 AM`),
            endDateTime: moment(`${moment().format('YYYY-MM-DD')} 12:00 PM`).add(2, 'days'),
            showSurvey: true
          }
        })
    },
    save (result) {
      var vm = this
      vm.loading = true
      Api().put('Course/' + result.course.id, result.course)
        .then((r) => {
          vm.courses.splice(vm.courses.findIndex((n) => n.id === result.course.id), 1, r.data)
        })
        .finally(() => {
          vm.loading = false
        })
    },
    copy (result) {
      var vm = this
      vm.loading = true
      Api().post(`Course/${result.course.id}/Copy`, result.course)
        .then((r) => {
          vm.courses.push(r.data)
        })
        .finally(() => {
          vm.loading = false
        })
    },
    deleteCourse (course) {
      this.$bvModal.msgBoxConfirm('Are you sure you want to delete this course?', {
        title: 'Delete Course?',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'YES',
        cancelTitle: 'NO',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      })
        .then(value => {
          if (value) {
            var vm = this
            vm.loading = true
            Api().delete('Course/' + course.id)
              .then((r) => {
                vm.courses.splice(vm.courses.findIndex((i) => { return i.id === course.id }), 1)
              })
              .catch((e) => {
                console.log(e)
              })
              .finally(() => {
                vm.loading = false
              })
          }
        })
    },
    clearFilter (filterName, filter) {
      this[filterName] = filter
      this.filterData(1)
    },
    sort (filter) {
      if (filter.sortOrder === 0) {
        filter.sortOrder = 1
      } else if (filter.sortOrder === 1) {
        filter.sortOrder = 2
      } else {
        filter.sortOrder = 0
      }
      this.filterData(1)
      console.log(filter)
    }
  },
  watch: {
    searchText (value) {
      this.titleFilter.text = value
      this.titleFilter.queryOperator = 2
      this.filterData(1)
    }
  },
  components: {
    CourseForm
  }
}
</script>

<style>

</style>
